import type { DeepPartial } from "@/types/utility";

export interface GetCompanyResponse {
  code: number;
  success: boolean;
  type: string;
  name: string;
  id: number;
  pk: string;
  subscription: GetCompanyResponseSubscription;
  resources: Resources;
  addons: Addon[];
  betas: string[];
  sms: SMS;
  setup: Setup;
  upgrade: Upgrade;
}

export interface Upgrade {
  status: UpgradeStatus | null;
  syncedAt: Date | null;
}

export enum UpgradeStatus {
  Eligible = "eligible",
  Synced = "synced",
  Scheduled = "scheduled",
  Completed = "completed",
}

export interface Addon {
  pk: string;
  name: string;
  trialUnits: number;
  enabled: boolean;
  subscription: AddonSubscription | null;
}

export interface AddonSubscription {
  status: string;
  quantity: number;
  trialEndAt: null;
  hadTrial: boolean;
  subscribedAt: string;
}

export interface Resources {
  ipads: number;
  locations: number;
  employees: number;
}

export interface Setup {
  brand: boolean;
  ipad: boolean;
  fields: boolean;
  employees: boolean;
  print: boolean;
  deliveries: boolean;
  complete: boolean;
}

export interface SMS {
  balance: number;
  smsEnabled: boolean;
}

export interface GetCompanyResponseSubscription {
  plan: Plan;
  status: string;
  resources: Resources;
  joinedAt: Date;
  renewalAt: Date;
  isManualCustomer: boolean;
  providerCompanyId: string;
}

export interface Plan {
  name: string;
  group: string;
  level: string;
}

export type BeverageCategoryOptions = {
  color: string;
  enabled: boolean;
  name: string;
  price: number;
  quantity: number;
  id?: string;
};

export type BeverageCategories = {
  enabled: boolean;
  multiple: boolean;
  name: string;
  required: boolean;
  options: BeverageCategoryOptions[];
};

export type Beverage = {
  enabled: boolean;
  image: string;
  name: string;
  quantity: number;
  options: BeverageCategories[];
};

export type Food = {
  enabled: boolean;
  image: string;
  imageName: string;
  name: string;
  notes: string;
  price: number;
};

export type CateringMenu = {
  name: string;
  enabled: boolean;
  notes: string;
  options: Beverage[] | Food[];
};

export enum MENU {
  BEVERAGE = 0,
  FOOD = 1,
}

export enum BEVERAGE {
  COFFEE = 0,
  TEA = 1,
  OTHER = 2,
}

export enum COFFEE_CATEGORY {
  TYPE = 0,
  MILK = 1,
  EXTRAS = 2,
}

export enum TEA_CATEGORY {
  TYPE = 0,
  MILK = 1,
  EXTRAS = 2,
}

export enum OTHER_CATEGORY {
  TYPE = 0,
  MILK = 1,
  EXTRAS = 2,
}

export type BEVERAGE_CATEGORY = COFFEE_CATEGORY | TEA_CATEGORY | OTHER_CATEGORY;

export type OrderContact = {
  email: string;
  firstName?: string;
  id?: string;
  image: string;
  lastName?: string;
  locationId: number | string;
  locationName: string;
  phone: string | null;
  role?: string;
  pk?: string;
  name?: string;
  isGeneralContact?: boolean;
};

export type DeliveryGeneral = {
  text: string;
};

export type DeliveryRecipient = {
  text: string;
};

export type DeliveryInstructions = {
  general: DeliveryGeneral[];
  recipient: DeliveryRecipient[];
};

export type CompanySettings = {
  company_field_label: string;
  company_field_state: "required" | "optional" | "disabled" | null;
  agreement_append: boolean;
  agreement_hide: boolean;
  agreement_type: string;
  slack_host_notifications: boolean;
  slack_visitor_approval_notifications: boolean;
  visits_require_signature: boolean;
  host_selection_custom_field: number | null;
  host_selection_department_field: boolean;
  employee_message_required: boolean;
  employee_remote: boolean;
  employee_roaming: boolean;
  emp_autologout: boolean;
  auto_logout_hour_employee: string | null;
  profile_capture_e: boolean;
  photo_capture_e: boolean;
  alert_notifications_enabled: boolean;
  email_global_cc: boolean;
  email_cc: string | null;
  hide_inactive_fields: boolean;
  host_approval_notifications: boolean;
  host_notifications_show_fields: boolean;
  host_notifications: boolean;
  photo_capture_v: boolean;
  show_employee_list: boolean;
  show_out_state: boolean;
  visit_review_show_fields: boolean;
  visiting_employee_toggle: boolean;
  visitor_auto_suggest: boolean;
  visitor_remember_me: boolean;
  alert_sms: string | null;
  alert_email: string | null;
  employee_secure_mode: boolean;
  show_employee_in_out_timestamp: boolean;
  mobile: {
    movements: boolean;
    beacon_notifications: boolean;
    proximity_movements: boolean;
    proximity_verification: boolean;
    proximity_out_verification: boolean;
    proximity_distance: number;
    preregistrations: boolean;
  };
  color_accent: string;
  print_label_number: number;
  visitor_enable_label_scan: boolean;
  addons: {
    catering: {
      menu: CateringMenu[];
      settings: {
        thankYou: {
          title: string;
          message: string;
        };
        hostReceivesOrder: boolean;
        orderContacts: OrderContact[];
      };
    };
    deliveries: {
      scanLabel: boolean;
      generalDeliveries: {
        enabled: boolean;
        contacts: OrderContact[];
      };
      instructions: DeliveryInstructions;
      imageKey: string;
    };
  };
  branding: {
    ipadPreview: {
      isPortrait?: boolean;
    };
  };
  visit_duration_notification?: boolean;
  visit_duration_notification_hours?: number;
  visit_duration_host_notification?: boolean;
  visit_out_notification?: boolean;
  visit_out_host_notification?: boolean;
  visitor_autologout?: boolean;
  auto_logout_hour_visitor?: string | null;
  auto_anonymise_days_visitor?: number;
  contactless: {
    gadget_enabled: boolean;
    static_host_selection: boolean;
  };
  ipad_scan_caption: string | null;
  ipad_radius: number;
  iPad_deliveries_btn_txt: string | null;
  iPad_emp_swipe_btn_txt: string | null;
  iPad_vis_in_txt: string | null;
  iPad_vis_out_txt: string | null;
  color_ipad_button_text: string | null;
  color_buttons: string | null;
  teams_host_notifications: boolean;
  teams_visitor_approval_notifications: boolean;
  categories_heading_enabled?: boolean;
  categories_heading?: string | null;
  mfa_enforcement?: boolean;
  sso_microsoft?: boolean;
  sso_google?: boolean;
  upgrade: {
    canceledAt: string | null;
    canceledBy: string | null;
    scheduledAt: string | null;
    scheduledBy: string | null;
  };
};

export type CompanySettingsApiResponse = {
  company_field_label: string;
  company_field_state: "required" | "optional" | "disabled" | null;
  agreement_append: 0 | 1;
  agreement_hide: 0 | 1;
  agreement_type: string;
  slack_host_notifications: 0 | 1;
  slack_visitor_approval_notifications: 0 | 1;
  visits_require_signature: 0 | 1;
  host_selection_custom_field: number | null;
  host_selection_department_field: 0 | 1;
  employee_message_required: 0 | 1;
  employee_remote: 0 | 1;
  employee_roaming: 0 | 1;
  emp_autologout: 0 | 1;
  auto_logout_hour_employee: string | null;
  profile_capture_e: 0 | 1;
  photo_capture_e: 0 | 1;
  alert_notifications_enabled: 0 | 1;
  email_global_cc: 0 | 1;
  email_cc: string | null;
  hide_inactive_fields: 0 | 1;
  host_approval_notifications: 0 | 1;
  host_notifications_show_fields: 0 | 1;
  host_notifications: 0 | 1;
  photo_capture_v: 0 | 1;
  show_employee_list: 0 | 1;
  show_out_state: 0 | 1;
  visit_review_show_fields: 0 | 1;
  visiting_employee_toggle: 0 | 1;
  visitor_auto_suggest: 0 | 1;
  visitor_remember_me: 0 | 1;
  alert_sms: string | null;
  alert_email: string | null;
  employee_secure_mode: 0 | 1;
  show_employee_in_out_timestamp: 0 | 1;
  mobile: {
    movements: 0 | 1;
    beacon_notifications: 0 | 1;
    proximity_verification: 0 | 1;
    proximity_out_verification: 0 | 1;
    proximity_movements: 0 | 1;
    proximity_distance: number;
    preregistrations: 0 | 1;
  };
  color_accent: string;
  print_label_number: number;
  visitor_enable_label_scan: 0 | 1;
  addons: {
    catering: {
      menu: {
        name: string;
        enabled: 0 | 1;
        notes: string;
        options: {
          name: string;
          enabled: 0 | 1;
          image: { key: string } | string;
          imageName: string;
          quantity: number;
          price: number;
          notes: string;
          options: {
            name: string;
            enabled: 0 | 1;
            required: 0 | 1;
            multiple: 0 | 1;
            options: {
              enabled: 0 | 1;
              name: string;
              color: string;
              price: number;
              quantity: number;
              id: string;
            }[];
          }[];
        }[];
      }[];
      settings: {
        thankYou: {
          title: string;
          message: string;
        };
        hostReceivesOrder: boolean;
        orderContacts: {
          name: string;
          email: string;
          phone: string;
          image: string;
          id?: string;
          value?: string;
          isGeneralContact?: boolean;
          locationName?: string;
          locationId?: string;
          firstName?: string;
          lastName?: string;
          photoKey?: string;
        }[];
      };
    };
    deliveries: {
      scanLabel: 0 | 1;
      generalDeliveries: {
        enabled: 0 | 1;
        contacts: {
          name: string;
          email: string;
          phone: string;
          image: string;
          id?: string;
          value?: string;
          isGeneralContact?: boolean;
          locationName?: string;
          locationId?: string;
          firstName?: string;
          lastName?: string;
          photoKey?: string;
        }[];
      };
      instructions: {
        general: {
          text: string;
        }[];
        recipient: {
          text: string;
        }[];
      };
      imageKey: string;
    };
  };
  branding: {
    ipadPreview: {
      isPortrait: boolean;
    };
  };
  visit_duration_notification: 0 | 1;
  visit_duration_notification_hours: number;
  visit_duration_host_notification: 0 | 1;
  visit_out_notification: 0 | 1;
  visit_out_host_notification: 0 | 1;
  visitor_autologout: 0 | 1;
  auto_logout_hour_visitor: string | null;
  auto_anonymise_days_visitor: number;
  contactless: {
    gadget_enabled: 0 | 1;
    static_host_selection: 0 | 1;
  };
  ipad_scan_caption: string | null;
  ipad_radius: number;
  iPad_deliveries_btn_txt: string | null;
  iPad_emp_swipe_btn_txt: string | null;
  iPad_vis_in_txt: string | null;
  iPad_vis_out_txt: string | null;
  color_ipad_button_text: string | null;
  color_buttons: string | null;
  teams_host_notifications: 0 | 1;
  teams_visitor_approval_notifications: 0 | 1;
  categories_heading_enabled: 0 | 1;
  categories_heading: string | null;
  mfa_enforcement: 0 | 1;
  sso_microsoft: 0 | 1;
  sso_google: 0 | 1;
  upgrade: {
    canceledAt: string | null;
    canceledBy: string | null;
    scheduledAt: string | null;
    scheduledBy: string | null;
  };
};

export type CompanySettingsApiRequest = DeepPartial<CompanySettingsApiResponse>;

export type GetCompanyApiResponse = {
  company: {
    settings: CompanySettingsApiResponse;
  };
};

export type UpdateCompanyApiRequest = {
  locationGroupPk?: string | null;
  settings: CompanySettingsApiRequest;
};

export type RegisterForm = {
  company: string;
  address?: string;
  dataRegion: string;
  terms: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  regionEndpoint?: string;
  recaptchaResult: string;
  howHeard: string;
};

export type CreateCompanyPayload = {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  timeZone: string;
  locale: string;
  marketplaceToken?: string;
  recaptchaResult: string;
};

export type CreateCompanyError = {
  error: {
    attribute: string;
    errorCode: string;
    message: string;
  };
};

export enum CreateCompanyErrorCode {
  EMAIL_INVALID = "email_invalid",
  MARKET_PLACE_TOKEN_INVALID = "marketplaceToken_invalid",
  MARKET_PLACE_TOKEN_UNKNOWN = "marketplaceToken_unknown",
  PHONE_INVALID = "phone_invalid",
  FORBIDDEN = "Forbidden",
}
